import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import ColumnInput from './ColumnInput'
import './editors.css'

const AGG_FUNCTIONS = [
  'count', 'sum', 'avg', 'min', 'max',
  'first', 'last',
  'collect_list', 'collect_set',
  'stddev', 'variance',
  'countDistinct', 'approx_count_distinct',
]

const EMPTY_AGG = { column: '', function: 'count', alias: '' }

function parseValue(raw) {
  if (!raw) return { group_by: [], aggregates: [] }
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return {
      group_by: Array.isArray(parsed.group_by) ? parsed.group_by : [],
      aggregates: Array.isArray(parsed.aggregates) ? parsed.aggregates : [],
    }
  } catch {
    return { group_by: [], aggregates: [] }
  }
}

function AggregationsEditor({ value, onChange, disabled, columns }) {
  const [groupInput, setGroupInput] = useState('')
  const state = parseValue(value)

  const emit = (next) => onChange(JSON.stringify(next))

  // Group By handlers
  const addGroupCol = (col) => {
    const colName = col || groupInput.trim()
    if (!colName) return
    emit({ ...state, group_by: [...state.group_by, colName] })
    setGroupInput('')
  }

  const removeGroupCol = (idx) => {
    emit({ ...state, group_by: state.group_by.filter((_, i) => i !== idx) })
  }

  // Aggregate handlers
  const updateAggregate = (idx, key, val) => {
    const aggregates = [...state.aggregates]
    aggregates[idx] = { ...aggregates[idx], [key]: val }
    emit({ ...state, aggregates })
  }

  const addAggregate = () => {
    emit({ ...state, aggregates: [...state.aggregates, { ...EMPTY_AGG }] })
  }

  const removeAggregate = (idx) => {
    emit({ ...state, aggregates: state.aggregates.filter((_, i) => i !== idx) })
  }

  return (
    <div>
      {/* Group By section */}
      <div className="editor-section-label">Group By</div>
      <div className="editor-group-tags">
        {state.group_by.map((col, idx) => (
          <span key={idx} className="editor-group-tag">
            {col}
            <button
              className="editor-group-tag-remove"
              onClick={() => removeGroupCol(idx)}
              disabled={disabled}
            >
              <X size={10} />
            </button>
          </span>
        ))}
      </div>
      <div className="editor-row" style={{ borderBottom: 'none' }}>
        {columns && columns.length > 0 ? (
          <ColumnInput
            columns={columns.filter(c => !state.group_by.includes(c.name))}
            value=""
            onChange={(val) => { if (val) addGroupCol(val) }}
            placeholder="Select column"
            disabled={disabled}
          />
        ) : (
          <input
            className="editor-input"
            value={groupInput}
            onChange={(e) => setGroupInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addGroupCol()
              }
            }}
            placeholder="Column name, press Enter to add"
            disabled={disabled}
          />
        )}
        {!(columns && columns.length > 0) && (
          <button
            className="editor-add-btn"
            onClick={() => addGroupCol()}
            disabled={disabled || !groupInput.trim()}
            style={{ width: 'auto', marginTop: 0, padding: '5px 10px' }}
          >
            <Plus size={12} />
          </button>
        )}
      </div>

      <hr className="editor-section-divider" />

      {/* Aggregates section */}
      <div className="editor-section-label">Aggregates</div>
      {state.aggregates.map((agg, idx) => (
        <div key={idx} className="editor-row">
          <ColumnInput
            columns={columns}
            value={agg.column}
            onChange={(val) => updateAggregate(idx, 'column', val)}
            placeholder="Column"
            disabled={disabled}
          />
          <select
            className="editor-select"
            value={agg.function || 'count'}
            onChange={(e) => updateAggregate(idx, 'function', e.target.value)}
            disabled={disabled}
          >
            {AGG_FUNCTIONS.map((fn) => (
              <option key={fn} value={fn}>{fn}</option>
            ))}
          </select>
          <input
            className="editor-input"
            value={agg.alias || ''}
            onChange={(e) => updateAggregate(idx, 'alias', e.target.value)}
            placeholder="Alias"
            disabled={disabled}
          />
          <button
            className="editor-remove-btn"
            onClick={() => removeAggregate(idx)}
            disabled={disabled}
            title="Remove aggregate"
          >
            <X size={12} />
          </button>
        </div>
      ))}

      {state.aggregates.length === 0 && (
        <div className="editor-empty">No aggregates defined</div>
      )}

      <button
        className="editor-add-btn"
        onClick={addAggregate}
        disabled={disabled}
      >
        <Plus size={12} />
        Add Aggregate
      </button>
    </div>
  )
}

export default AggregationsEditor
